"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = AddToCartButton;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _merchButton = require("@site/pattern-library-core/merch-button");
var _react2 = require("@linaria/react");
var _reactIntersectionObserver = require("react-intersection-observer");
var _patternLibraryUtils = require("@site/pattern-library-utils");
var _precheck = require("../precheck");
var _index = _interopRequireDefault(require("../../../experiments/express-cart-modal/index.js"));
var _atcUtils = require("./atc-utils.js");
var _base = require("@site/pattern-library-utils/base.functions");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/* eslint-disable max-statements */

const AddToCartForm = /*#__PURE__*/(0, _react2.styled)("form")({
  name: "AddToCartForm",
  class: "pl-a1hvuo8n"
});

/**
  * Render action buttons
  * @module AddToCartButton
  *
  * @param {Object} props React props for element
  * @param {String} props.action [Required] Url for adding to cart
  * @param {String} props.itemTrackingCode [Required] ITC (item tracking code)
  * @param {String} props.productPackage [Required] Product Package ID
  * @param {String} props.productPackageTermLengthInMonths [Required] Product Package term length specified in months
  * @param {Array} props.addOnItems Array of strings of add on items
  * @param {String} props.source Source field is used by the config system to control what initial config display to show.
  * @param {('cart'|'config'|'freedomain'|'basket'|'precheck')} props.destination Optional override to send user to a destination
  * @param {String} props.itemSourceCode ISC (item source code) optional if you want to override the value of the query or cookie
  * @param {Array} props.prefetchAssets Array of strings of asset URLs
  * @param {Boolean} props.prefetchCartAssets Prefetch cart assets
  * @param {Boolean} props.passThroughQueryParams Pass through query params
  * @param {Boolean} props.isHeaderCart Is header cart
  * @param {String} props.expressCartBanner Express cart banner
  * @param {String} props.loadingMessage Expresscart Loading message
  * @param {String} props.nsMerchButtonProps NSMerchButton props
  * @returns {React.ReactElement} UXCore ButtonSet component
  * @public
  */
// eslint-disable-next-line complexity
function AddToCartButton({
  id,
  actionKey = '',
  action,
  itemTrackingCode,
  isc,
  source = 'ac',
  destination,
  productPackage,
  productPackageTermLengthInMonths,
  addOnItems,
  width = 'auto',
  isHeaderCart = false,
  prefetchAssets = [],
  prefetchCartAssets = false,
  passThroughQueryParams = false,
  expressCartBanner,
  loadingMessage,
  errorRedirectUrl,
  ...nsMerchButtonProps
}) {
  const [prefetched, setPrefetched] = (0, _react.useState)(false);
  const trackingCustomProperties = {};
  const isServerSideRender = (0, _patternLibraryUtils.useIsServerSideRender)();
  const term = productPackageTermLengthInMonths ? `${productPackageTermLengthInMonths}:Month` : null;
  const {
    ref,
    inView
  } = (0, _reactIntersectionObserver.useInView)({
    initialInView: false
  });
  const [guid, setGuid] = (0, _react.useState)('');
  const handleError = error => (0, _atcUtils.redirectOnError)(error, errorRedirectUrl);
  const addToCartUrl = new URL(action);
  const {
    searchParams
  } = addToCartUrl;
  const location = typeof window !== 'undefined' ? window?.location?.href : null;
  const {
    searchParams: pageSearchParams
  } = passThroughQueryParams && location ? new URL(location) : {};
  if (pageSearchParams) {
    pageSearchParams.forEach((value, key) => {
      searchParams.set(key, value);
    });
  }
  const itc = itemTrackingCode || searchParams.get('itc');
  if (source) {
    searchParams.set('src', source);
  }
  if (destination) {
    searchParams.set('destination', destination);
  }
  if (isc) {
    searchParams.set('isc', isc);
  }
  if (itc) {
    searchParams.set('itc', itc);
    trackingCustomProperties.item_tracking_code = itc;
  }
  if (destination === 'expressCart') {
    searchParams.set('basketId', guid);
  }
  let itemTag;
  if (!isServerSideRender && destination === 'precheck') {
    itemTag = (0, _base.generateGUID)();
    searchParams.set('itemTag', itemTag);
  }
  (0, _react.useEffect)(() => {
    if (inView && prefetchCartAssets && !prefetched) {
      (0, _patternLibraryUtils.fetchAssets)(prefetchAssets);
      setPrefetched(true);
    }
  }, [inView, prefetchCartAssets, prefetched, prefetchAssets]);

  // ATC Button Logic Setup
  let addToCartButton;
  if (isHeaderCart) {
    addToCartButton = /*#__PURE__*/_react.default.createElement(_merchButton.NSMerchButton, (0, _extends2.default)({
      id: id,
      "data-itc": itc,
      "data-cy": "add-to-cart-button",
      onClick: () => {
        (0, _patternLibraryUtils.addToHeaderCart)({
          productPackage,
          term,
          itc: itc,
          actionKey,
          addToCartUrl: action,
          onError: handleError
        });
      },
      width: width,
      type: "button",
      trackingCustomProperties: trackingCustomProperties
    }, nsMerchButtonProps));
  } else if (destination === 'precheck') {
    addToCartButton = /*#__PURE__*/_react.default.createElement(_precheck.PrecheckCta, (0, _extends2.default)({
      id: id,
      "data-itc": itc,
      "data-cy": "precheck-button",
      width: width,
      type: "submit",
      trackingCustomProperties: trackingCustomProperties,
      addToCartUrl: addToCartUrl.href,
      isServerSideRender: isServerSideRender,
      onError: handleError
    }, nsMerchButtonProps));
  } else if (destination === 'expressCart') {
    addToCartButton = /*#__PURE__*/_react.default.createElement(_index.default, (0, _extends2.default)({
      id: id,
      width: width,
      trackingCustomProperties: trackingCustomProperties,
      guid: guid,
      setGuid: setGuid,
      productPackage: productPackage,
      term: term,
      itc: itc,
      addToCartUrl: addToCartUrl,
      expressCartBanner: expressCartBanner,
      loadingMessage: loadingMessage,
      shrinkOn: "mobile",
      onError: handleError
    }, nsMerchButtonProps));
  } else {
    addToCartButton = /*#__PURE__*/_react.default.createElement(_merchButton.NSMerchButton, (0, _extends2.default)({
      id: id,
      "data-itc": itc,
      "data-cy": "add-to-cart-button",
      width: width,
      type: "submit",
      trackingCustomProperties: trackingCustomProperties
    }, nsMerchButtonProps));
  }
  return /*#__PURE__*/_react.default.createElement(AddToCartForm, {
    ref: ref,
    id: `id-${id}`,
    method: "post",
    "data-width": width,
    action: addToCartUrl.href,
    "data-cy": "add-to-cart-form"
  }, /*#__PURE__*/_react.default.createElement("input", {
    type: "hidden",
    name: "pkgid",
    value: productPackage
  }), term && /*#__PURE__*/_react.default.createElement("input", {
    type: "hidden",
    name: "term",
    value: term
  }), /*#__PURE__*/_react.default.createElement("input", {
    type: "hidden",
    name: "itc",
    value: itc
  }), itemTag && /*#__PURE__*/_react.default.createElement("input", {
    type: "hidden",
    name: "itemTag",
    value: itemTag
  }), addOnItems && addOnItems.map((item, idx) => /*#__PURE__*/_react.default.createElement("input", {
    key: `addon${idx}`,
    type: "hidden",
    name: "addOns",
    value: item
  })), addToCartButton);
}
AddToCartButton.propTypes = {
  id: _propTypes.default.string,
  actionKey: _propTypes.default.string,
  itemTrackingCode: _propTypes.default.string.isRequired,
  productPackage: _propTypes.default.string.isRequired,
  action: _propTypes.default.string.isRequired,
  productPackageTermLengthInMonths: _propTypes.default.string,
  addOnItems: _propTypes.default.array,
  source: _propTypes.default.oneOf(['ac', 'gs', 'gc']),
  destination: _propTypes.default.oneOf(['cart', 'config', 'freedomain', 'precheck', 'basket', 'expressCart']),
  isc: _propTypes.default.string,
  width: _propTypes.default.oneOf(['auto', 'full']),
  isHeaderCart: _propTypes.default.bool,
  prefetchAssets: _propTypes.default.arrayOf(_propTypes.default.string),
  prefetchCartAssets: _propTypes.default.bool,
  passThroughQueryParams: _propTypes.default.bool,
  expressCartBanner: _propTypes.default.object,
  loadingMessage: _propTypes.default.string,
  errorRedirectUrl: _propTypes.default.string
};